import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { Spin } from "antd";
import { RequestDetail } from "@companion-core/shared/app/Components/Requests/RequestDetail";
import { RequestDetailContent } from "@companion-core/shared/app/Components/Requests/RequestDetail/requestDetail.model";
import { APIRequest } from "@companion-core/shared/app/Interfaces/API";
import { useRequest } from "@companion-core/shared/app/Queries";
import { slugifyRequest } from "@companion-core/shared/app/Utils/myRequests";
import "Assets/Styles/scss/myRequests.scss";

const Detail = () => {
  const navigate = useNavigate();
  const { id, content, contentId } = useParams();
  const { data: requestData, isPending, isError } = useRequest(id ?? "", !!id);
  const request = useMemo(
    () => slugifyRequest(requestData?.data?.result as APIRequest),
    [requestData],
  ); // TODO: [SLUGIFY_REQUEST] simplify like: const request = useMemo(() => (data?.data?.result as APIRequest), [data]);
  const navigateToDetailContent = useCallback(
    (to: RequestDetailContent) => {
      if (id && to) {
        navigate(`/my-requests/${id}/details/${to}`, { state: { canGoBack: true } });
      }
    },
    [id],
  );
  const navigateToDetailRecommendation = useCallback(
    (proposalId: string, recommendationId: string) => {
      if (id) {
        navigate(`/my-requests/${id}/proposals/${proposalId}/recommendations/${recommendationId}`, {
          state: { canGoBack: true },
        });
      }
    },
    [id],
  );
  const navigateToEditor = useCallback(
    (action: string) => {
      if (id && action) {
        navigate(`/my-requests/${id}/editor/${action}`, { state: { canGoBack: true } });
      }
    },
    [id],
  );
  const goBackByDetailContent = useCallback(() => {
    switch (content) {
      case "detail":
        return navigate("/my-requests");
      case "proposal":
        return navigateToDetailContent(RequestDetailContent.detail);
      case "recommendation":
        return navigateToDetailContent(RequestDetailContent.proposals);
      case "proposals":
        if (contentId) return navigateToDetailContent(RequestDetailContent.proposals);
        return navigateToDetailContent(RequestDetailContent.detail);
      default:
        return navigateToDetailContent(RequestDetailContent.detail);
    }
  }, [content, navigate, navigateToDetailContent]);

  useEffect(() => {
    if (content && !(content in RequestDetailContent)) {
      navigate(`/my-requests/${id}/details/detail`, { replace: true, state: { canGoBack: true } });
    }

    if (!request && !isPending) {
      navigate("/my-requests");
    }
  }, [request, isPending]);

  useEffect(() => {
    if (isError) {
      navigate("/my-requests");
    }
  }, [isError]);

  return request && id ? (
    <RequestDetail
      data={request}
      id={id}
      contentId={contentId}
      detailContent={(content ?? RequestDetailContent.detail) as RequestDetailContent}
      navigateToDetailContent={navigateToDetailContent}
      navigateToDetailRecommendation={navigateToDetailRecommendation}
      navigateToEditor={navigateToEditor}
      goBack={goBackByDetailContent}
    />
  ) : (
    <div className="my-requests__no-request">
      <Spin data-testid="my-requests__loader" size="large" />
    </div>
  );
};

export default Detail;
